import { Type } from '@sinclair/typebox';
import { SupportedDexSchema } from '../supported-dex.js';
import { SupportedNetworkSchema } from '../supported-network.js';
import { TokenModerationReasonSchema, TokenModerationStatusSchema, } from '../token/token-moderation.js';
export const TxOperationSchema = Type.Union([
    Type.Literal('buy'),
    Type.Literal('sell'),
    Type.Literal('poolInit'),
    Type.Literal('liquidityAdd'),
    Type.Literal('liquidityRemove'),
    Type.Literal('poolMigrate'),
    Type.Literal('lpBurn'),
    Type.Literal('quoteBurn'),
]);
export const TxTokenSchema = Type.Object({
    address: Type.String(),
    name: Type.String(),
    symbol: Type.String(),
    decimals: Type.Number(),
    supply: Type.String(),
    logo: Type.Optional(Type.String()),
    isRenounced: Type.Optional(Type.Boolean()),
    tokenProgram: Type.Optional(Type.String()),
    mintAuthority: Type.Optional(Type.String()),
    freezeAuthority: Type.Optional(Type.String()),
    updateAuthority: Type.Optional(Type.String()),
    status: Type.Optional(TokenModerationStatusSchema),
    moderationReason: Type.Optional(TokenModerationReasonSchema),
    imageHash: Type.Optional(Type.String({
        description: 'Perceptual dHash of the token logo (64-bit hex)',
    })),
    reusedImageCount: Type.Optional(Type.Number({
        description: 'Number of other tokens sharing the same logo image',
    })),
});
const TxExtraDetailsCommonSchema = Type.Object({
    poolState: Type.String(),
    creatorFee: Type.String(),
    migrated: Type.Optional(Type.Boolean()),
});
export const TokenizedAgentSchema = Type.Object({
    authority: Type.String(), // Pubkey base58
    paymentsAccount: Type.String(), // token_agent_payments, base58
    buybackFeeBasisPoints: Type.Union([Type.String(), Type.Number()]),
    sequence: Type.String(), // tokenized_agent_sequence (u64) — string, overflows JS number
    activatedAt: Type.String(), // timestamp (unix i64) → ISO
});
const TxExtraDetailsPumpfunSchema = Type.Object({
    ...TxExtraDetailsCommonSchema.properties,
    virtualBaseReserves: Type.String(),
    virtualQuoteReserves: Type.String(),
    isMayhemMode: Type.Optional(Type.Boolean()),
    isCashbackMode: Type.Optional(Type.Boolean()),
    buybackFeeBasisPoints: Type.Optional(Type.Union([Type.String(), Type.Number()])),
    tokenizedAgent: Type.Optional(TokenizedAgentSchema),
});
const TxExtraDetailsRaydiumSchema = Type.Object({
    ...TxExtraDetailsCommonSchema.properties,
    platformFee: Type.String(),
    shareFee: Type.String(),
    tradeDirection: Type.String(),
    realQuoteBefore: Type.String(),
    exactIn: Type.String(),
    realBaseAfter: Type.String(),
    virtualBase: Type.String(),
    amountIn: Type.String(),
    virtualQuote: Type.String(),
    realBaseBefore: Type.String(),
    realQuoteAfter: Type.String(),
    poolStatus: Type.String(),
    amountOut: Type.String(),
    totalBaseSell: Type.String(),
    protocolFee: Type.String(),
    creatorFee: Type.String(),
});
export const TxExtraDetailsSchema = Type.Union([
    TxExtraDetailsPumpfunSchema,
    TxExtraDetailsRaydiumSchema,
]);
export const BaseTxSchema = Type.Object({
    fromDex: SupportedDexSchema,
    txSignature: Type.String(),
    operation: TxOperationSchema,
    maker: Type.String(),
    pairAddress: Type.String(),
    pair: Type.Tuple([Type.String(), Type.String()]),
    baseToken: TxTokenSchema,
    quoteToken: TxTokenSchema,
    baseAmount: Type.String(),
    quoteAmount: Type.String(),
    network: SupportedNetworkSchema,
    hasMMAlert: Type.Boolean(),
    poolQuoteBalanceAfter: Type.String(),
    poolBaseBalanceAfter: Type.String(),
    marketCap: Type.String({
        description: 'Market cap of the token. Can be missing if token is very new',
    }),
    liquidityInUsd: Type.String(),
    tokenPriceInUsd: Type.String(),
    tokenPriceInNative: Type.String(),
    volumeInUsd: Type.String(),
    timestamp: Type.String(),
    block: Type.Number(),
    blockSubIndex: Type.Optional(Type.Number()),
    programId: Type.Optional(Type.String()),
    effectiveProgramId: Type.Optional(Type.String()),
    nativePriceInUsd: Type.String(),
    balanceBefore: Type.String(),
    balanceAfter: Type.String(),
    balanceChanged: Type.String(),
    balanceChangedPercent: Type.String(),
    fee: Type.Optional(Type.String({
        description: 'Native solana fee for consumed units. Paid to solana validators',
    })),
    priorityFee: Type.Optional(Type.String({
        description: 'Native solana fee for faster landing. Paid to solana validators',
    })),
    bundleTipFee: Type.Optional(Type.String({
        description: 'Bundle fee for faster landing. Paid to providers like jito, 0slot',
    })),
    computeUnitsConsumed: Type.Optional(Type.Number()),
    extraDetails: Type.Optional(TxExtraDetailsSchema),
});
//# sourceMappingURL=base-tx.js.map